import { clip, type Clip } from '../clip.ts';
import { TAU, inOut, inQuad, outBack, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * El Dragón Ahogado (models/sunken.ts): `idle` (the bones sway as if the sea still held them, the jaw working, the
 * neck weaving), `walk` (a heavy, lurching four-beat crawl, the neck low and the wings' stumps rocking), `attack` (it
 * rears and rakes a fore claw down), `inhale` (drawing breath: the neck pulled back and up, the jaw closing, the ribs
 * swelling round the fire), `breath` (the neck snapping forward low, the jaw wide), `held` (a harpoon in it: braced,
 * straining against the rope, the head thrashing), `hit`, `stun`, `rise` (climbing out of the sea) and `die` (the
 * bones come apart: it slumps and the neck falls).
 *
 * Conventions as the uro's: limbs hang along −Y, a negative X swings them forward; +X on a lower leg folds the knee
 * back; +X on the body pitches it nose-down about the hind hips; +X on the neck or head drops it; +X on the jaw opens
 * it; the wings fold in on −Z (left) / +Z (right) and spread with the opposite.
 */

type B = 'root' | 'body' | 'neck' | 'head' | 'jaw' | 'tail' | 'tail2' | 'foreL' | 'foreL2' | 'foreR' | 'foreR2' | 'hindL' | 'hindL2' | 'hindR' | 'hindR2' | 'wingL' | 'wingR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** The jaw hung a little open, the wings half folded, the tail low. */
const ST: P = { jaw: [0.18, 0, 0], tail: [0.05, 0, 0], tail2: [0.1, 0, 0], wingL: [0, 0, -0.15], wingR: [0, 0, 0.15] };
const LAG: Partial<Record<B, number>> = { neck: 0.05, head: 0.09, tail: 0.08, tail2: 0.15, wingL: 0.1, wingR: 0.1 };

const idle = (): Clip<B> =>
  clip<B>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    apply(b, ST);
    const sw = Math.sin(p);
    b.root.pos(0.03 * sw, 0, 0).rot(0, 0, 0.015 * sw);
    b.body.rot(0.02 * Math.sin(p * 2), 0.03 * sw, 0.02 * sw);
    b.neck.rot(0.06 * Math.sin(p + 0.6), 0.18 * Math.sin(p * 0.5), 0.05 * sw);
    b.head.rot(0.08 * Math.sin(p * 2 + 1), -0.12 * Math.sin(p * 0.5 + 0.4), 0.06 * Math.sin(p + 1));
    b.jaw.rot(0.16 + 0.12 * Math.max(0, Math.sin(p * 2)), 0, 0);
    b.tail.rot(0.04, 0.22 * Math.sin(p + 1.2), 0);
    b.tail2.rot(0.1, 0.3 * Math.sin(p + 2), 0);
    b.wingL.rot(0.05 * Math.sin(p + 0.5), 0, -0.15 - 0.08 * Math.sin(p));
    b.wingR.rot(0.05 * Math.sin(p + 0.5), 0, 0.15 + 0.08 * Math.sin(p));
  });

/** A heavy crawl: diagonal pairs, the body rolling over each step, the neck low and swinging. */
const walk = (dur = 1.9): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    apply(b, ST, 0.8);
    const leg = (up: B, lo: B, off: number, A: number, fold: number): void => {
      const a = th - off * TAU;
      b[up].rot(-A * Math.sin(a) - 0.02);
      b[lo].rot(fold * Math.max(0, Math.cos(a)) ** 1.5);
    };
    leg('hindL', 'hindL2', 0, 0.38, 0.6);
    leg('foreR', 'foreR2', 0.08, 0.42, 0.75);
    leg('hindR', 'hindR2', 0.5, 0.38, 0.6);
    leg('foreL', 'foreL2', 0.58, 0.42, 0.75);
    const land = Math.max(0, -Math.cos(2 * th)) ** 3;
    b.root.pos(0.04 * Math.sin(th), -0.04 * land, 0).rot(0, 0.03 * Math.sin(th), 0.04 * Math.sin(th - 0.4));
    b.body.rot(0.03 + 0.02 * Math.sin(2 * th + 0.5), 0.05 * Math.sin(th), 0.04 * Math.sin(th - 0.6));
    b.neck.rot(0.12 + 0.05 * Math.sin(2 * th), -0.15 * Math.sin(th - 0.3), 0);
    b.head.rot(-0.05 + 0.05 * Math.sin(2 * th - 0.8), -0.1 * Math.sin(th - 0.8), 0.04 * Math.sin(th));
    b.jaw.rot(0.15 + 0.08 * land, 0, 0);
    b.tail.rot(0.05, 0.25 * Math.sin(th - 1.2), 0);
    b.tail2.rot(0.1, 0.35 * Math.sin(th - 2), 0);
    b.wingL.rot(0.08 * Math.sin(th), 0, -0.15 - 0.1 * Math.sin(th));
    b.wingR.rot(-0.08 * Math.sin(th), 0, 0.15 - 0.1 * Math.sin(th));
  }, (t) => {
    const k = Math.max(0, -Math.cos(2 * phase(t, dur) * TAU)) ** 3;
    return [1 + 0.012 * k, 1 - 0.015 * k, 1 + 0.012 * k];
  });

/** It rears on its hind legs and rakes the right fore claw down, the jaw snapping. */
const attack = (): Clip<B> =>
  clip<B>('attack', 1.1, false, (b, t) => {
    const up: P = { ...ST, body: [-0.28, 0, 0], neck: [-0.25, 0, 0], head: [0.1, 0, 0], jaw: [0.6, 0, 0], foreR: [-1.1, 0, 0.1], foreR2: [0.7, 0, 0], foreL: [-0.4, 0, 0], foreL2: [0.4, 0, 0], hindL: [0.2, 0, 0], hindR: [0.2, 0, 0], tail: [-0.2, 0, 0], wingL: [0, 0, -0.5], wingR: [0, 0, 0.5] };
    const rake: P = { ...ST, body: [0.12, 0, 0], neck: [0.25, 0, 0], head: [0.15, 0, 0], jaw: [0.05, 0, 0], foreR: [0.35, 0, -0.05], foreR2: [0.1, 0, 0], foreL: [-0.05, 0, 0], hindL: [-0.08, 0, 0], hindR: [-0.08, 0, 0], tail: [0.15, 0, 0], wingL: [0, 0, 0.1], wingR: [0, 0, -0.1], root: [0, 0, 0, 0, -0.04, 0.15] };
    timeline(b, t, [
      [0, ST],
      [0.42, up, inOut],
      [0.58, rake, outExpo],
      [1.1, ST, inOut],
    ], LAG);
  }, (t) => {
    const k = span(t, 0.42, 0.6, outQuad) * (1 - span(t, 0.6, 1.0));
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  });

/** Drawing breath: the neck pulled back and high, the jaw shut, the ribs swelling round the fire, braced. */
const inhale = (): Clip<B> =>
  clip<B>('inhale', 1.5, false, (b, t) => {
    const k = span(t, 0, 1.2, inOut);
    const sh = Math.sin(t * 40) * 0.015 * k;
    apply(b, ST);
    b.body.rot(-0.12 * k, sh, 0).scale(1 + 0.06 * k);
    b.neck.rot(-0.45 * k, 0, sh);
    b.head.rot(-0.2 * k, 0, 0);
    b.jaw.rot(0.18 * (1 - k), 0, 0);
    b.foreL.rot(-0.15 * k);
    b.foreR.rot(-0.15 * k);
    b.hindL.rot(0.1 * k);
    b.hindR.rot(0.1 * k);
    b.wingL.rot(0, 0, -0.15 - 0.45 * k);
    b.wingR.rot(0, 0, 0.15 + 0.45 * k);
    b.tail.rot(-0.15 * k, 0, 0);
  }, (t) => {
    const k = span(t, 0, 1.2, inOut);
    return [1 + 0.03 * k, 1 + 0.02 * k, 1 + 0.03 * k];
  });

/** The breath: the neck thrown forward and low, the jaw wide, held while the fire goes. */
const breath = (): Clip<B> =>
  clip<B>('breath', 1.4, false, (b, t) => {
    const out: P = { ...ST, body: [0.12, 0, 0], neck: [0.4, 0, 0], head: [-0.15, 0, 0], jaw: [0.9, 0, 0], foreL: [0.1, 0, 0], foreR: [0.1, 0, 0], hindL: [-0.12, 0, 0], hindR: [-0.12, 0, 0], wingL: [0, 0, 0.25], wingR: [0, 0, -0.25], tail: [0.2, 0, 0] };
    timeline(b, t, [
      [0, { ...ST, body: [-0.12, 0, 0], neck: [-0.45, 0, 0], head: [-0.2, 0, 0], jaw: [0, 0, 0], wingL: [0, 0, -0.6], wingR: [0, 0, 0.6] }],
      [0.18, out, outExpo],
      [1.0, out],
      [1.4, ST, inOut],
    ], LAG);
    const shake = t > 0.18 && t < 1.0 ? Math.sin(t * 50) * 0.03 : 0;
    b.head.rot(-0.15 + shake * (t < 1 ? 1 : 0), shake, 0);
  });

/** A harpoon in it: braced back against the rope, the head thrashing, the claws dug in. */
const held = (): Clip<B> =>
  clip<B>('held', 1.0, true, (b, t) => {
    const ph = phase(t, 1.0);
    apply(b, ST);
    const j1 = kick(ph, 0.05, 6, 9);
    const j2 = kick(ph, 0.5, 6, 9);
    b.root.pos(0, -0.05, -0.1 * Math.abs(j1 + j2));
    b.body.rot(-0.1 + 0.05 * j1, 0.08 * j2, 0.06 * (j1 - j2));
    b.neck.rot(-0.2 + 0.25 * j2, 0.35 * j1 - 0.3 * j2, 0);
    b.head.rot(0.2 * j1, 0.3 * j2, 0.2 * j1);
    b.jaw.rot(0.5 + 0.3 * Math.abs(j1), 0, 0);
    b.foreL.rot(0.25, 0, -0.08);
    b.foreR.rot(0.25, 0, 0.08);
    b.foreL2.rot(0.3);
    b.foreR2.rot(0.3);
    b.hindL.rot(-0.2);
    b.hindR.rot(-0.2);
    b.tail.rot(-0.3, 0.4 * Math.sin(ph * TAU * 2), 0);
    b.tail2.rot(0, 0.5 * Math.sin(ph * TAU * 2 + 1), 0);
    b.wingL.rot(0, 0, -0.5 - 0.2 * j1);
    b.wingR.rot(0, 0, 0.5 + 0.2 * j2);
  });

/** A blow it barely feels: the bones rattle. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.04, outQuad);
    b.body.rot(-0.03 * k, 0.03 * k, 0.03 * k);
    b.neck.rot(-0.15 * k, 0.1 * k, 0);
    b.head.rot(-0.2 * k, 0.12 * k, 0.08 * k);
  });

/** Stunned: sagging, the head hung low and lolling, the jaw slack. */
const stun = (): Clip<B> =>
  clip<B>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    apply(b, ST);
    b.root.pos(0, -0.12, 0);
    b.body.rot(0.12, 0, 0.05 * Math.sin(p));
    b.neck.rot(0.45, 0.15 * Math.sin(p), 0);
    b.head.rot(0.3, 0.2 * Math.sin(p + 1), 0.2 * Math.sin(p));
    b.jaw.rot(0.55, 0, 0);
    b.foreL2.rot(0.35);
    b.foreR2.rot(0.35);
    b.wingL.rot(0, 0, 0.2);
    b.wingR.rot(0, 0, -0.2);
  });

/** Climbing out of the sea: low and dragging, then up on its legs, the neck rising last. */
const rise = (): Clip<B> =>
  clip<B>('rise', 2.0, false, (b, t) => {
    const low: P = { ...ST, root: [0, 0, 0, 0, -1.2, -0.4], body: [0.3, 0, 0], neck: [0.5, 0, 0], head: [0.2, 0, 0], foreL: [-0.8, 0, 0], foreR: [-0.6, 0, 0], foreL2: [1.0, 0, 0], foreR2: [0.9, 0, 0], wingL: [0, 0, 0.3], wingR: [0, 0, -0.3] };
    timeline(b, t, [
      [0, low],
      [0.8, { ...low, root: [0, 0, 0, 0, -0.5, -0.1], body: [0.1, 0, 0], neck: [0.2, 0, 0] }, outQuad],
      [1.4, { ...ST, neck: [-0.4, 0, 0], head: [-0.2, 0, 0], jaw: [0.8, 0, 0], wingL: [0, 0, -0.7], wingR: [0, 0, 0.7] }, outBack],
      [2.0, ST, inOut],
    ], LAG);
  });

/** Felled: the fire goes out, the legs give and the bones slump into a heap, the neck falling last. */
const die = (): Clip<B> =>
  clip<B>('die', 2.4, false, (b, t) => {
    const slump: P = { root: [0, 0, 0.15, 0, -0.75, 0], body: [0.2, 0, 0.15], neck: [0.7, 0.3, 0.1], head: [0.4, 0.2, 0.3], jaw: [0.7, 0, 0], foreL: [-0.6, 0, -0.2], foreL2: [1.6, 0, 0], foreR: [-0.5, 0, 0.3], foreR2: [1.5, 0, 0], hindL: [-0.9, 0, -0.2], hindL2: [1.8, 0, 0], hindR: [-0.8, 0, 0.25], hindR2: [1.7, 0, 0], wingL: [0.3, 0, 0.6], wingR: [0.3, 0, -0.7], tail: [0.3, 0.3, 0], tail2: [0.2, 0.5, 0] };
    timeline(b, t, [
      [0, ST],
      [0.35, { ...ST, body: [-0.2, 0, 0], neck: [-0.6, 0, 0], head: [-0.3, 0, 0], jaw: [1.0, 0, 0], wingL: [0, 0, -0.8], wingR: [0, 0, 0.8] }, outExpo],
      [1.4, { ...slump, neck: [0.1, 0.1, 0], root: [0, 0, 0.1, 0, -0.6, 0] }, inQuad],
      [2.4, slump, outBack],
    ], LAG);
  });

export const sunkenClips = (): Clip[] => [idle(), walk(), attack(), inhale(), breath(), held(), hit(), stun(), rise(), die()] as Clip[];

/** Clips by library kind (see ../rigs.ts): with its fire, and with the steam (no heart). */
export const CLIPS: Record<string, () => Clip[]> = { sunken: sunkenClips, sunkenSteam: sunkenClips };
